import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin } from "lucide-react";
import { api } from "@/lib/api";

const initial = { name: "", company: "", email: "", phone: "", tonnage: "", message: "" };

const inputCls = "w-full bg-white border border-ink/15 px-4 py-3.5 text-base text-ink placeholder:text-slate-400 focus:outline-none focus:border-chrome transition-colors duration-300";

export const Contact = () => {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry sent. Our team will respond shortly.");
      setForm(initial);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send your enquiry. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-chrome mb-6">Enquiries</p>
          <h2 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl md:text-6xl leading-[0.95] text-ink" data-testid="contact-heading">
            LET'S MOVE<br />CHROME.
          </h2>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-slate-600 max-w-md">
            Tell us your tonnage and specification — we respond to every enquiry,
            from spot parcels to annual contracts, anywhere in the world.
          </p>
          <div className="mt-12 space-y-6">
            <a data-testid="contact-phone-link" href="tel:+27823706110" className="flex items-center gap-4 group">
              <span className="w-11 h-11 border border-ink/15 flex items-center justify-center text-chrome group-hover:bg-chrome group-hover:text-white group-hover:border-chrome transition-colors duration-300"><Phone size={16} /></span>
              <span className="text-base text-ink group-hover:text-chrome transition-colors duration-300">082 370 6110</span>
            </a>
            <a data-testid="contact-email-link" href="mailto:Obrigadoptyltd@gmail.com" className="flex items-center gap-4 group">
              <span className="w-11 h-11 border border-ink/15 flex items-center justify-center text-chrome group-hover:bg-chrome group-hover:text-white group-hover:border-chrome transition-colors duration-300"><Mail size={16} /></span>
              <span className="text-base text-ink group-hover:text-chrome transition-colors duration-300 break-all">Obrigadoptyltd@gmail.com</span>
            </a>
            <div data-testid="contact-location" className="flex items-center gap-4">
              <span className="w-11 h-11 border border-ink/15 flex items-center justify-center text-chrome"><MapPin size={16} /></span>
              <span className="text-base text-ink">South Africa — serving clients globally</span>
            </div>
          </div>
        </motion.div>
        <motion.form
          data-testid="enquiry-form"
          onSubmit={submit}
          className="lg:col-span-7 bg-surface border border-ink/10 p-8 md:p-12"
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, delay: 0.15 }}
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label htmlFor="enquiry-name" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Full Name *</label>
              <input id="enquiry-name" data-testid="enquiry-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-company" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Company</label>
              <input id="enquiry-company" data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Company / organisation" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-email" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Email *</label>
              <input id="enquiry-email" data-testid="enquiry-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@company.com" className={inputCls} />
            </div>
            <div>
              <label htmlFor="enquiry-phone" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Phone</label>
              <input id="enquiry-phone" data-testid="enquiry-phone-input" value={form.phone} onChange={set("phone")} placeholder="+27 ..." className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-tonnage" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Required Tonnage</label>
              <input id="enquiry-tonnage" data-testid="enquiry-tonnage-input" value={form.tonnage} onChange={set("tonnage")} placeholder="e.g. 5,000 tons / month" className={inputCls} />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="enquiry-message" className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500 block mb-2">Message *</label>
              <textarea id="enquiry-message" data-testid="enquiry-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="Tell us about your requirements — grade, volume, delivery terms..." className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="enquiry-submit-button"
            type="submit"
            disabled={sending}
            className="mt-8 bg-ink text-paper font-mono text-[11px] uppercase tracking-[0.2em] px-8 py-4 hover:bg-chrome disabled:opacity-50 transition-colors duration-300"
          >
            {sending ? "Sending..." : "Send Enquiry"}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
